import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PatchData, TasksQueryParams } from '../types';

export interface PaginationOptionsWithFilter extends Partial<TasksQueryParams>, PaginationOptions {}

export interface PaginationOptions {
  pageSize?: number;
  pageNumber?: number;
  sortBy?: string;
  isAscending?: boolean;
}

export type ApiParams<T = object> = PaginationOptions & T;

/**
 * BaseApiService is a generic service that provides common HTTP operations
 * for working with resources in an API.
 *
 * @template T - The type of the entity returned by the API for GET requests.
 *                This type represents the structure of the resource you will
 *                receive from the server.
 *
 * @template T1 - The type of the entity to be sent to the API for GET by Id
 *                requests. This type represents the structure of the resource
 *                you will recieve for sent type from the server.
 *
 * @template T2 - The type of the entity to be sent to the API for POST and PUT
 *                requests. This type represents the structure of the resource
 *                you will create or update on the server.
 */
@Injectable()
export abstract class BaseApiService<T, T1, T2> {
  public abstract controllerName(): string;

  protected readonly trackerKeyHeader = 'X-Lazer-Track-API';

  constructor(
    public http: HttpClient,
    public api: string,
  ) {}

  public get<U = object>(options?: ApiParams<U>): Observable<T> {
    let params = new HttpParams();
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value) {
          params = params.append(key, value);
        }
      });
    }

    return this.http.get<T>(`${this.api}/${this.controllerName()}`, {
      params,
    });
  }

  public getById(id: string): Observable<T1> {
    return this.http.get<T1>(`${this.api}/${this.controllerName()}/${id}`);
  }

  public insert(entity: unknown): Observable<T2> {
    const headers = new HttpHeaders().set(this.trackerKeyHeader, `insert/${this.controllerName()}`);
    return this.http.post<T2>(`${this.api}/${this.controllerName()}`, entity, {
      headers: headers,
    });
  }

  public update(id: string, entity: unknown): Observable<T2> {
    const headers = new HttpHeaders().set(this.trackerKeyHeader, `update/${this.controllerName()}/${id}`);

    return this.http.put<T2>(`${this.api}/${this.controllerName()}/${id}`, entity, {
      headers: headers,
    });
  }

  public patch(entity: PatchData[]): Observable<T2> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set(this.trackerKeyHeader, `patch/${this.controllerName()}`);

    return this.http.patch<T2>(
      `${this.api}/${this.controllerName()}`,
      {
        patches: entity,
      },
      {
        headers,
      },
    );
  }

  public delete(id: number | string): Observable<boolean> {
    const headers = new HttpHeaders().set(this.trackerKeyHeader, `delete/${this.controllerName()}/${id}`);
    return this.http.delete<boolean>(`${this.api}/${this.controllerName()}/${id}`, {
      headers: headers,
    });
  }
}
